import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import qs from 'query-string';
import Joi from 'joi';

import { useForgotPasswordMutation, useResetPasswordMutation } from 'api/main';
import { sanitizeURL, validateSchema } from 'utils/helpers';
import { PASS_REGEX } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { SubmitButton, PasswordField } from 'components';
import TunnelStep from 'containers/tunnel/common/TunnelStep';
import TunnelBox from 'containers/tunnel/common/TunnelBox';
import { Typography, Box } from '@mui/material';

const inputSx = theme => ({ width: '100%', [theme.breakpoints.up('sm')]: { width: '60%' } });

const RESET_PASSWORD_QUERY_PARAMS = ['code', 'email'];

const schema = {
    password: Joi.string().pattern(PASS_REGEX).required().messages({
        'string.empty': texts.EMPTY_NEW_PASSWORD,
        'string.pattern.base': texts.LE_MOT_DE_PASSE_DOIT_CONTENIR,
    }),
    confirmPassword: Joi.when('password', {
        is: '',
        then: Joi.any(),
        otherwise: Joi.ref('password'),
    }).messages({ 'any.only': texts.PASSWORDS_MUST_MATCH }),
};

const ResetPassword = () => {
    const navigate = useNavigate();

    const sanitizedURL = useMemo(
        () =>
            sanitizeURL({
                url: window.location.href,
                paramList: RESET_PASSWORD_QUERY_PARAMS,
            }),
        []
    );
    const sanitizedSearch = useMemo(() => new URL(sanitizedURL).search, [sanitizedURL]);

    const [forgotPassword, { isSuccess: emailSent, isLoading: requestLoader }] =
        useForgotPasswordMutation();
    const [
        resetPassword,
        { isLoading: submitLoader, isSuccess: successfulReset, isError: linkExpired },
    ] = useResetPasswordMutation();

    const { email, code } = useMemo(
        () => qs.parse(sanitizedSearch, { decode: true }),
        [sanitizedSearch]
    );

    useEffect(() => {
        if (sanitizedURL !== window.location.href) {
            window.history.pushState({}, null, sanitizedURL);
        }
    }, [sanitizedURL]);
    const [form, setForm] = useState({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({
        password: '',
        confirmPassword: '',
    });

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        return valid;
    }, [form]);

    const handleChange = key => e => {
        const { value } = e.target;
        setForm(form => ({ ...form, [key]: value }));
    };

    const handleResetPassword = useCallback(() => {
        if (formIsValid()) {
            resetPassword({ email, code, password: form.password });
        }
    }, [resetPassword, email, code, form, formIsValid]);

    const handleResendEmail = useCallback(
        e => {
            e.preventDefault();
            forgotPassword(email);
        },
        [forgotPassword, email]
    );

    useEffect(() => {
        if (!code || !email) {
            navigate('/sign-in');
        }
    }, [code, email, navigate]);

    return (
        <Box width="100%" pt="90px">
            <TunnelStep title={texts.REINITIALISATION_DU_MOT_DE_PASSE}>
                <TunnelBox>
                    {!successfulReset && !linkExpired && !emailSent && (
                        <>
                            <Box
                                onSubmit={e => e.preventDefault()}
                                sx={{
                                    width: '100%',
                                    marginTop: '30px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <PasswordField
                                    formControlProps={{ sx: inputSx }}
                                    label={texts.NOUVEAU_MOT_DE_PASSE}
                                    value={form.password}
                                    onChange={handleChange('password')}
                                    error={errors.password}
                                />
                                <PasswordField
                                    formControlProps={{ sx: inputSx }}
                                    label={texts.CONFIRM_NEW_PASSWORD}
                                    value={form.confirmPassword}
                                    onChange={handleChange('confirmPassword')}
                                    error={errors.confirmPassword}
                                />
                                <SubmitButton
                                    text={texts.CHANGER_LE_MOT_DU_PASSE}
                                    onClick={handleResetPassword}
                                    disabled={submitLoader}
                                    loading={submitLoader}
                                />
                            </Box>
                            <Link to="/sign-in">{texts.SE_CONNECTER}</Link>
                        </>
                    )}
                    {successfulReset && (
                        <Typography
                            align="center"
                            display="block"
                            gutterBottom
                            variant="body1"
                            color="success.main"
                        >
                            {texts.MOT_DE_PASSE_A_ETE_CHANGE}{' '}
                            <Link to="/sign-in">{texts.CLICK_HERE}</Link>{' '}
                            {texts.POUR_VOUS_AUTHENTIFIER}
                        </Typography>
                    )}
                    {linkExpired && !emailSent && (
                        <Typography
                            align="center"
                            display="block"
                            gutterBottom
                            variant="body1"
                            color="error.main"
                        >
                            {texts.LIEN_POUR_REINITIALISATION_DU_MOT_DU_PASSE_EXPIRE}{' '}
                            <Link
                                to="/forgot-password"
                                disabled={requestLoader}
                                onClick={handleResendEmail}
                            >
                                {texts.CLICK_HERE}
                            </Link>{' '}
                            {texts.POUR_RENVOYER_UN_NOUVEAU_EMAIL}
                        </Typography>
                    )}
                    {emailSent && (
                        <Typography variant="body1" align="left" color="success.main">
                            {texts.UN_NOUVEAU_MESSAGE_VOUS_SERA_ENVOYE}
                        </Typography>
                    )}
                </TunnelBox>
            </TunnelStep>
        </Box>
    );
};

export default ResetPassword;
